import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';
import { Checkbox,
  ListItem,
  ListItemButton,
  } from '@mui/material';
import { styled } from '@mui/material/styles';

type Item = {
  id: number | string;
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: 0,
  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
  },
}));

type Props<T extends Item> = {
  items: T[];
  selectedIds: (number | string)[];
  isSelected: (id: number | string) => boolean;
  onToggle: (id: number | string) => void;
  renderItem: (
    item: T,
    selected: boolean,
    selectionIndex: number
  ) => React.ReactNode;
  height?: number;
};

const ROW_HEIGHT = 80;

export default function VirtualizedItemList<T extends Item>({
  items,
  selectedIds,
  isSelected,
  onToggle,
  renderItem,
  height = 500,
}: Props<T>) {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  });

  return (
    <div
      ref={parentRef}
      style={{
        height,
        overflow: 'auto',
      }}
    >
      <div
        style={{
          height: rowVirtualizer.getTotalSize(),
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map(virtualRow => {
          const item = items[virtualRow.index];
          const selected = isSelected(item.id);
          const selectionIndex = selected
            ? selectedIds.indexOf(item.id) + 1
            : -1;

          return (
            <StyledListItem
              key={item.id}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <StyledListItemButton
                selected={selected}
                onClick={() => onToggle(item.id)}
                sx={{ alignItems: 'flex-start' }}
              >
                <Checkbox
                  edge="start"
                  checked={selected}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                  sx={{ pt: 0.5 }}
                />

                {renderItem(item, selected, selectionIndex)}
              </StyledListItemButton>
            </StyledListItem>
          );
        })}
      </div>
    </div>
  );
}
